import React, { useState, useImperativeHandle, forwardRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  useListDockerCreds,
  useCreateDockerCred,
  useDeleteDockerCred,
} from '@/services/dockerCreds'
import { Trash2, Container, Eye, EyeOff } from 'lucide-react'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const FormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  docker_username: z.string().min(1, 'Username is required'),
  docker_password: z.string().min(1, 'Password is required'),
})

export interface SetupDockerCredsDialogRef {
  openDialog: () => void
}

export const SetupDockerCredsDialog = forwardRef<SetupDockerCredsDialogRef>(
  (_, ref) => {
    const [open, setOpen] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState<number | false>(
      false
    )
    const [showPassword, setShowPassword] = useState(false)

    useImperativeHandle(ref, () => ({
      openDialog: () => setOpen(true),
    }))

    const form = useForm<z.infer<typeof FormSchema>>({
      resolver: zodResolver(FormSchema),
      defaultValues: {
        name: '',
        docker_username: '',
        docker_password: '',
      },
    })

    const {
      data: dockerCreds = [],
      isLoading: isCredsLoading,
      error: credsError,
    } = useListDockerCreds()

    const { mutateAsync: createDockerCred, isPending: isCreating } =
      useCreateDockerCred()

    const { mutate: deleteDockerCred, isPending: isDeleting } =
      useDeleteDockerCred()

    const onSubmit = async (values: z.infer<typeof FormSchema>) => {
      await createDockerCred(values, {
        onSuccess: () => form.reset(),
      })
    }

    const handleDelete = () => {
      if (typeof openDeleteDialog === 'number') {
        deleteDockerCred(openDeleteDialog, {
          onSuccess: () => setOpenDeleteDialog(false),
        })
      }
    }

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-[500px]"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Docker Credentials</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Add credentials for private Docker registries. These can be used
              when deploying servers with custom Docker images.
            </p>
          </DialogHeader>

          {/* Add Credential Form */}
          <div className="border rounded-lg p-4 bg-muted/10 space-y-3 my-4">
            <h3 className="text-sm font-medium">Add New Credential</h3>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-3"
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Docker Hub"
                          className="h-8 text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="docker_username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Username</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Docker username"
                          className="h-8 text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="docker_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">
                        Password / Access Token
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Docker password or access token"
                            className="h-8 text-sm pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-8 w-8"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button type="submit" size="sm" disabled={isCreating}>
                    {isCreating ? (
                      <span className="mr-2">
                        <LoadingMessage size="sm" />
                      </span>
                    ) : null}
                    {isCreating ? 'Adding...' : 'Add Credential'}
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Credentials List */}
          {(isCredsLoading || dockerCreds.length > 0) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">
                Your Credentials ({dockerCreds.length})
              </h3>

              <div className="space-y-2">
                {isCredsLoading ? (
                  <div className="flex justify-center py-4">
                    <LoadingMessage message="Loading credentials..." />
                  </div>
                ) : (
                  dockerCreds.map((cred) => (
                    <div
                      key={cred.id}
                      className="flex items-center space-x-2 rounded-md border p-2 bg-muted/5 hover:bg-muted/10 transition-colors"
                    >
                      <div className="bg-primary/10 p-1.5 rounded">
                        <Container className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {cred.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {cred.docker_username}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive opacity-60 hover:opacity-100"
                        onClick={() => setOpenDeleteDialog(cred.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {credsError?.message && (
            <ErrorMessage message={credsError.message} className="mt-4" />
          )}

          {/* Footer */}
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>

          {/* Delete Confirmation */}
          <ConfirmationDialog
            open={!!openDeleteDialog}
            onOpenChange={(open) => {
              if (!isDeleting) {
                setOpenDeleteDialog(open ? openDeleteDialog : false)
              }
            }}
            title="Delete Credential"
            description="Are you sure you want to delete this Docker credential? This action cannot be undone."
            buttons={{
              confirm: {
                label: 'Delete',
                isDestructive: true,
                isLoading: isDeleting,
              },
              cancel: { label: 'Cancel' },
            }}
            onConfirm={handleDelete}
          />
        </DialogContent>
      </Dialog>
    )
  }
)
